import React, { Fragment, useEffect, useMemo, useState } from "react";
import Controls from "./controls";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "./table.head";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "./table.row";
import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import { useStudyPlan } from "../../app/contexts/study.context";
import { useUser } from "../../app/contexts/user.context";
import TableHeadFilter from "./table.head/filter";
import { debounce } from "lodash";
import moment from "moment";
import { getComparator } from "../../app/helpers/utils";

const ROWS_PER_PAGE_KEY = "ctpo_study_plan_table_rows_per_page";
const defaultRowsPerPage = () => {
  const stored = localStorage.getItem(ROWS_PER_PAGE_KEY);
  return stored ? parseInt(stored, 10) : 5;
};

const StudyTable = () => {
  const { study } = useStudyPlan();
  const [filter, setFilter] = useState("");
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("name");
  const [rowsPerPage, setRowsPerPage] = React.useState(defaultRowsPerPage());
  const [page, setPage] = React.useState(0);
  const [filterActive, setFilterActive] = useState(false);
  const [filters, setFilters] = useState({});
  const studyPlans = study.studyPlans || [];
  const [filteredStudyPlans, setFilteredStudyPlans] = useState(studyPlans);
  const [hasStudyPlans, setHasStudyPlans] = useState(false);
  const classes = useStyles();
  const { isReadOnlyUser, setURLStudyId } = useUser();
  const { id } = useParams();

  const debounceFilter = useMemo(
    () =>
      debounce((lFilters) => {
        const filterKeys = Object.keys(lFilters);
        if (!filterKeys.length) return setFilteredStudyPlans(study.studyPlans || []);
        // for each filter, filter projects using the id and value and set the filtered projects

        const filtered = (study.studyPlans || []).filter((plan) => {
          return filterKeys.every((filterId) => {
            const { value, type, start, end } = lFilters[filterId];
            if (type === "date") {
              if (!start || !end) return true;
              const startDate = moment(start).valueOf();
              const endDate = moment(end).valueOf();
              if (!plan[filterId]) return false;
              const projectDate = moment(plan[filterId]).valueOf();
              let startD = new Date(startDate);
              let endD = new Date(endDate);
              let projectD = new Date(projectDate);

              startD = new Date(
                startD.getFullYear(),
                startD.getMonth(),
                startD.getDate(),
              );
              endD = new Date(
                endD.getFullYear(),
                endD.getMonth(),
                endD.getDate(),
              );
              projectD = new Date(
                projectD.getFullYear(),
                projectD.getMonth(),
                projectD.getDate(),
              );

              if (projectD >= startD && projectD <= endD) return true;
              else return false;
            }

            if (type === "select") {
              if (!plan[filterId]) return false;
            }

            if (plan[filterId]) {
              return plan[filterId].toLowerCase().includes(value.toLowerCase());
            } else {
              return false;
            }
          });
        });
        setFilteredStudyPlans(filtered);
        setPage(0);
      }, 500),
    [study.studyPlans],
  );

  useEffect(() => {
    debounceFilter(filters);
    
    // Cleanup: cancel debounced function on unmount
    return () => {
      debounceFilter.cancel();
    };
  }, [debounceFilter, filters]);

  useEffect(() => {
    setFilteredStudyPlans(studyPlans);
  }, [studyPlans]);

  useEffect(() => {
    setURLStudyId(id ?? 3712);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const hasStudyPlans = filteredStudyPlans.some((row) => row.studyPlanId);
    setHasStudyPlans(hasStudyPlans);
  }, [filteredStudyPlans]);

  /**
   *  Sorting Fuctions
   */
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  /**
   * Pagination
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const { value } = event.target;
    const rows = parseInt(value);

    setRowsPerPage(rows);
    setPage(0);

    localStorage.setItem(ROWS_PER_PAGE_KEY, rows);
  };

  return (
    <Fragment>
      {!isReadOnlyUser ? (
        <Controls
          search={filter}
          onSearch={(value) => setFilter(value)}
          clearSearch={() => setFilter("")}
          onNewPlan={(noop) => noop}
        />
      ) : (
        <br />
      )}
      <Paper style={{ border: "1px solid var(--surface-300)" }}>
        <Paper style={{ minHeight: "100%" }}>
          <TableContainer className={classes.root}>
            <Table
              size="medium"
              aria-label="study table"
              className={hasStudyPlans ? "" : classes.disabled}
            >
              <TableHead
                order={order}
                orderBy={orderBy}
                onRequestSort={handleRequestSort}
                filterActive={filterActive}
                setFilterActive={setFilterActive}
              />
              {filterActive && (
                <TableHeadFilter
                  filters={filters}
                  setFilters={setFilters}
                  filterActive={filterActive}
                  setFilterActive={setFilterActive}
                />
              )}
              <TableBody>
                {stableSort(filteredStudyPlans, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow
                      key={row.studyPlanId}
                      {...row}
                      isReadOnlyUser={isReadOnlyUser}
                      studyPlans={filteredStudyPlans?.studyPlans || []}
                    />
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {hasStudyPlans && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              rowsPerPage={rowsPerPage}
              page={page}
              count={filteredStudyPlans.length || 0}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          )}
        </Paper>
      </Paper>
    </Fragment>
  );
};

export default StudyTable;

const useStyles = makeStyles((theme) => ({
  root: {
    borderRadius: "4px",
  },
  disabled: {
    pointerEvents: "none",
    opacity: 0.7,
  },
}));
